/**
 * useAiProposalReviewDashboard — Issue #394.
 *
 * Read-only hook that fetches the AI proposal draft / review / customer
 * publication dashboard from the backend. The dashboard is rendered in
 * Admin web at /proposals/ai-review-dashboard.
 *
 * 红线：
 *   - 数据全部走真实后端 typed client，不允许伪造或前端假状态。
 *   - 未审核 / 已撤回的内容仅以状态形式展示，绝不会泄露 AI 草稿正文。
 */
import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type AiProposalReviewDashboardDto =
  ApiComponents['schemas']['AiProposalReviewDashboardDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, status?: number): ApiError {
  const httpStatus = status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (httpStatus === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (httpStatus === 403) {
    return { message: '当前角色没有 AI 方案审核看板的访问权限', code: 'FORBIDDEN' };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

export function useAiProposalReviewDashboard() {
  const [snapshot, setSnapshot] = useState<AiProposalReviewDashboardDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(async (limit?: number) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/ai-proposal-review-dashboard',
        {
          params: {
            query: limit ? { limit } : {},
          },
        },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setSnapshot(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { snapshot, loading, error, refresh };
}
